import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { LLink as Link } from '../components/LocalizedLink';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import { postsApi, uploadsApi } from '../lib/api';
import { articleToSafeHtml } from '../lib/pasteSanitizer';
import { useI18n } from '../context/I18nContext';

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    postsApi.getPublic(slug, lang)
      .then((p) => setPost(p))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  if (loading) return <div className="container-x py-24 text-center text-slate-500">Loading...</div>;
  if (notFound) return <Navigate to="/blog" replace />;
  if (!post) return null;

  const cover = post.cover_image ? uploadsApi.publicUrl(post.cover_image) : null;
  const date = new Date(post.updated_at || post.created_at).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div>
      <Helmet>
        <title>{post.seo_title || post.title}</title>
        {post.seo_description && <meta name="description" content={post.seo_description} />}
        <meta property="og:title" content={post.seo_title || post.title} />
        {post.seo_description && <meta property="og:description" content={post.seo_description} />}
        <meta property="og:type" content="article" />
        {cover && <meta property="og:image" content={cover} />}
      </Helmet>

      <section className="hero-bg">
        <div className="container-x pt-14 pb-10 max-w-3xl">
          <Link to="/blog" className="text-sm text-slate-500 hover:text-[#0d3a8a] inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={14} /> Back to blog
          </Link>
          {post.tag && <span className="eyebrow">{post.tag}</span>}
          <h1 className="mt-4 text-4xl md:text-5xl font-bold leading-tight text-[#0b1f3a] tracking-tight">
            {post.title}
          </h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1"><Calendar size={14} /> {date}</span>
            {post.tag && <span className="inline-flex items-center gap-1"><Tag size={14} /> {post.tag}</span>}
          </div>
        </div>
      </section>

      {cover && (
        <div className="container-x max-w-4xl -mt-6">
          <img src={cover} alt={post.title} className="w-full h-[380px] object-cover rounded-2xl shadow-lg" />
        </div>
      )}

      <section className="py-14">
        <div className="container-x max-w-3xl">
          {post.excerpt && (
            <p className="text-lg text-slate-600 leading-relaxed border-l-4 border-[#0d3a8a] pl-5 mb-8">
              {post.excerpt}
            </p>
          )}
          <article
            className="prose-esi"
            dangerouslySetInnerHTML={{ __html: articleToSafeHtml(post.content || '') }}
          />
        </div>
      </section>

      <section className="py-14 bg-[#f7fafd]">
        <div className="container-x max-w-3xl text-center">
          <h3 className="text-2xl font-bold text-[#0b1f3a]">Ready to save on your Spanish insurance?</h3>
          <p className="mt-2 text-slate-600">Compare quotes from top insurers in under 10 minutes.</p>
          <Link to="/home-insurance" className="btn-primary mt-5 inline-flex">Get your quote</Link>
        </div>
      </section>
    </div>
  );
}
